import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, CardContent, Typography, Box, Button, Collapse, IconButton,
  List, ListItem, ListItemIcon, ListItemText, Chip, Divider, CircularProgress,
  Alert
} from '@mui/material';
import {
  AutoAwesome, Close, ExpandMore, ExpandLess, Settings, Refresh,
  Warning, Info, CheckCircle, TrendingUp, Security
} from '@mui/icons-material';
import api from '../../services/api';
import { status } from '../../theme/colors';

/**
 * Smart Dashboard Assistant with Hybrid Intelligence
 * - Real data from database
 * - Relevance scoring based on user behavior
 * - Contextual recommendations (time-based, workflow-based)
 * - Personalized insights
 */
const SmartDashboardAssistant = ({ onDismiss, onOpenSettings }) => {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hiddenCount, setHiddenCount] = useState(0);
  const retriedRef = React.useRef(false);

  // Fetch smart insights from backend
  useEffect(() => {
    fetchSmartInsights();
  }, []);

  const fetchSmartInsights = async (refresh = false) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/smart-insights/dashboard', {
        params: refresh ? { refresh: 'true' } : {}
      });

      if (response.data.success) {
        setInsights(response.data.data.insights || []);
        setHiddenCount(response.data.data.hiddenCount || 0);
        retriedRef.current = false;
      } else {
        console.error('API returned unsuccessful response:', response.data);
        setError('Failed to load insights');
      }
    } catch (err) {
      console.error('Error fetching smart insights:', err);
      // Don't show error if it's just a network issue or 404 - might be expected
      if (err.response?.status === 404 || err.code === 'ECONNREFUSED') {
        setError(null); // Silently fail - insights are optional
        setInsights([]);
      } else if (!retriedRef.current) {
        // Auto-retry once after 3 seconds for transient errors
        retriedRef.current = true;
        setTimeout(() => fetchSmartInsights(), 3000);
        return; // Keep loading state while retrying
      } else {
        setError('Failed to load insights');
      }
    } finally {
      setLoading(false);
    }
  };

  // Track user interaction
  const trackInteraction = async (insight, action) => {
    try {
      await api.post('/smart-insights/track', {
        insightType: insight.type,
        insightCategory: insight.category,
        assistantType: 'dashboard',
        action: action,
        insightData: insight.data
      });
    } catch (err) {
      console.error('Error tracking interaction:', err);
    }
  };

  // Handle insight click
  const handleInsightClick = (insight) => {
    trackInteraction(insight, 'clicked');
    if (insight.actionRoute) {
      navigate(insight.actionRoute);
    }
  };

  // Handle dismiss
  const handleDismiss = () => {
    setDismissed(true);
    if (onDismiss) onDismiss();
  };

  // Get icon for insight
  const getInsightIcon = (category, type) => {
    if (category === 'urgent') return <Warning sx={{ color: '#F04747' }} />;
    if (category === 'important') return <Info sx={{ color: '#FAA61A' }} />;
    if (type === 'fraud_alert') return <Security sx={{ color: '#F04747' }} />;
    if (type.includes('prediction') || type.includes('forecast')) return <TrendingUp sx={{ color: status.info }} />;
    return <CheckCircle sx={{ color: '#43B581' }} />;
  };

  // Get color for insight
  const getInsightColor = (category, type) => {
    if (category === 'urgent' || type === 'fraud_alert') return '#F04747';
    if (category === 'important') return '#FAA61A';
    return status.info;
  };

  // Group insights by category
  const groupedInsights = {
    urgent: insights.filter(i => i.category === 'urgent'),
    important: insights.filter(i => i.category === 'important'),
    suggestion: insights.filter(i => i.category === 'suggestion')
  };

  const totalInsights = insights.length;

  if (dismissed) {
    return null;
  }

  if (loading) {
    return (
      <Card
        sx={{
          borderRadius: 2,
          mb: 3,
          border: `2px solid ${status.info}`,
          bgcolor: `${status.info}10`
        }}
      >
        <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress size={30} sx={{ color: status.info }} />
          <Typography variant="body2" sx={{ ml: 2, color: '#666' }}>
            Analyzing your dashboard...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert
        severity="warning"
        sx={{ mb: 3 }}
        onClose={handleDismiss}
        action={
          <Button color="inherit" size="small" onClick={() => fetchSmartInsights(true)}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (totalInsights === 0) {
    return null;
  }

  return (
    <Card
      sx={{
        borderRadius: 2,
        mb: 3,
        border: `2px solid ${status.info}`,
        bgcolor: `${status.info}10`
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesome sx={{ color: status.info, fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Smart Insights
            </Typography>
            <Chip
              label={`${totalInsights} insight${totalInsights > 1 ? 's' : ''}`}
              size="small"
              sx={{ bgcolor: status.info, color: 'white', fontWeight: 600 }}
            />
            {hiddenCount > 0 && (
              <Chip
                label={`+${hiddenCount} more`}
                size="small"
                variant="outlined"
                sx={{ borderColor: status.info, color: status.info, fontWeight: 600 }}
              />
            )}
          </Box>
          <Box>
            <IconButton size="small" onClick={() => fetchSmartInsights(true)} title="Refresh insights" disabled={loading}>
              <Refresh sx={{ fontSize: 20 }} />
            </IconButton>
            {onOpenSettings && (
              <IconButton size="small" onClick={onOpenSettings} title="AI Settings">
                <Settings />
              </IconButton>
            )}
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
            <IconButton size="small" onClick={handleDismiss}>
              <Close />
            </IconButton>
          </Box>
        </Box>

        <Collapse in={expanded}>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            Good {new Date().getHours() < 12 ? 'morning' : 'afternoon'}! Here's what needs your attention today:
          </Typography>

          {/* Urgent */}
          {groupedInsights.urgent.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#F04747', mb: 1 }}>
                🔴 URGENT ({groupedInsights.urgent.length})
              </Typography>
              <List sx={{ bgcolor: 'white', borderRadius: 1 }}>
                {groupedInsights.urgent.map((insight, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      borderLeft: `4px solid ${getInsightColor(insight.category, insight.type)}`,
                      mb: 1,
                      borderRadius: 1,
                      cursor: insight.action ? 'pointer' : 'default',
                      '&:hover': insight.action ? {
                        bgcolor: '#f5f5f5'
                      } : {}
                    }}
                    onClick={() => insight.action && handleInsightClick(insight)}
                  >
                    <ListItemIcon>{getInsightIcon(insight.category, insight.type)}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {insight.text}
                        </Typography>
                      }
                      secondary={insight.subtitle}
                    />
                    {insight.action && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleInsightClick(insight);
                        }}
                        sx={{
                          textTransform: 'none',
                          borderColor: getInsightColor(insight.category, insight.type),
                          color: getInsightColor(insight.category, insight.type),
                          fontWeight: 600
                        }}
                      >
                        {insight.action}
                      </Button>
                    )}
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Important */}
          {groupedInsights.important.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#FAA61A', mb: 1 }}>
                🟡 IMPORTANT ({groupedInsights.important.length})
              </Typography>
              <List sx={{ bgcolor: 'white', borderRadius: 1 }}>
                {groupedInsights.important.map((insight, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      borderLeft: `4px solid ${getInsightColor(insight.category, insight.type)}`,
                      mb: 1,
                      borderRadius: 1,
                      cursor: insight.action ? 'pointer' : 'default',
                      '&:hover': insight.action ? {
                        bgcolor: '#f5f5f5'
                      } : {}
                    }}
                    onClick={() => insight.action && handleInsightClick(insight)}
                  >
                    <ListItemIcon>{getInsightIcon(insight.category, insight.type)}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {insight.text}
                        </Typography>
                      }
                      secondary={insight.subtitle}
                    />
                    {insight.action && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleInsightClick(insight);
                        }}
                        sx={{
                          textTransform: 'none',
                          borderColor: getInsightColor(insight.category, insight.type),
                          color: getInsightColor(insight.category, insight.type),
                          fontWeight: 600
                        }}
                      >
                        {insight.action}
                      </Button>
                    )}
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Suggestions */}
          {groupedInsights.suggestion.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: status.info, mb: 1 }}>
                💡 SUGGESTIONS ({groupedInsights.suggestion.length})
              </Typography>
              <List sx={{ bgcolor: 'white', borderRadius: 1 }}>
                {groupedInsights.suggestion.map((insight, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      borderLeft: `4px solid ${getInsightColor(insight.category, insight.type)}`,
                      mb: index < groupedInsights.suggestion.length - 1 ? 1 : 0,
                      borderRadius: 1,
                      cursor: insight.action ? 'pointer' : 'default',
                      '&:hover': insight.action ? {
                        bgcolor: '#f5f5f5'
                      } : {}
                    }}
                    onClick={() => insight.action && handleInsightClick(insight)}
                  >
                    <ListItemIcon>{getInsightIcon(insight.category, insight.type)}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {insight.text}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          {insight.subtitle}
                        </Typography>
                      }
                    />
                    {insight.action && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleInsightClick(insight);
                        }}
                        sx={{
                          textTransform: 'none',
                          color: getInsightColor(insight.category, insight.type),
                          fontWeight: 600
                        }}
                      >
                        {insight.action} →
                      </Button>
                    )}
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: '#666' }}>
              🧠 AI-powered insights personalized for you
            </Typography>
            <Button
              size="small"
              onClick={handleDismiss}
              sx={{ textTransform: 'none', color: '#666' }}
            >
              Dismiss
            </Button>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default SmartDashboardAssistant;
